/**
 * Cody → People: who Cody DMs for each topic (first one active on Slack wins, then down the list, then the work-group channel),
 * the confidence bar under which Auto runs go to a person instead, and the log of Slack conversations and what they turned into.
 */
import { useState, useEffect, useCallback } from "react";
import { Loader2, Send, X, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { codyTalk, codySaveTalk, codySlackUsers, codyConversations, codyTalkTest } from "@/lib/api";

const TOPIC_BLURB = {
  sales: "top-up candidates, customer replies — 'ok to email these?'",
  underwriting: "broker queries on offers, Sumsub results — 'broker asks for 24 weeks, OK?'",
  processing: "IR replies and documents — 'post the note and move it on?'",
  collections: "weekly top-up endorsements — 'all except 2 and 5'",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

export function PeopleTab() {
  const [talk, setTalk] = useState(null);
  const [users, setUsers] = useState([]);
  const [convs, setConvs] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    codyTalk().then(setTalk).catch(() => err(null, "Failed to load routing"));
    codySlackUsers().then(setUsers).catch(() => {});
    codyConversations().then(setConvs).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!talk) return <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto my-4" />;
  const name = (id) => users.find(u => u.id === id)?.name || id;
  const save = async (patch, ok) => {
    setBusy("save");
    try { setTalk(t => ({ ...t, ...patch })); await codySaveTalk(patch); toast.success(ok || "Saved"); } catch (e) { err(e, "Save failed"); load(); } finally { setBusy(""); }
  };
  const addPerson = (topic, id) => { if (!id || talk.routing[topic].includes(id)) return; save({ routing: { ...talk.routing, [topic]: [...talk.routing[topic], id] } }, `${name(id)} added to ${topic}`); };
  const removePerson = (topic, id) => save({ routing: { ...talk.routing, [topic]: talk.routing[topic].filter(x => x !== id) } }, `${name(id)} removed from ${topic}`);
  const test = async (topic) => {
    setBusy(topic);
    try { const r = await codyTalkTest(topic); toast.success(r.sent ? `Test DM sent to ${r.to.name} (${r.to.reason})` : "Slack not configured — nothing sent"); load(); } catch (e) { err(e, "Test failed"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3" data-testid="cody-people">
      <div className="text-[11px] text-slate-500">
        When Cody is not sure, or a job is on <b>Propose</b>, it DMs the first person on the topic's list who is <b>active on Slack right now</b>, then the next, then the work-group channel.
        Their reply in that DM is the instruction — "yep", "all except 2", "offer 26 weeks", "leave it with me" — and Cody acts on it, logged with their name.
      </div>
      <div className="border rounded-lg p-3 flex items-center gap-3 text-[11px]" data-testid="cody-confidence">
        <div className="font-semibold text-slate-800 text-xs">Confidence bar</div>
        <input type="range" min={50} max={100} step={5} value={talk.confidence_threshold} className="w-48 accent-violet-600"
          onChange={(e) => setTalk(t => ({ ...t, confidence_threshold: Number(e.target.value) }))}
          onMouseUp={(e) => save({ confidence_threshold: Number(e.target.value) }, `Confidence bar ${e.target.value}%`)}
          onTouchEnd={() => save({ confidence_threshold: talk.confidence_threshold })}
          onKeyUp={(e) => save({ confidence_threshold: Number(e.target.value) }, `Confidence bar ${e.target.value}%`)} data-testid="cody-confidence-slider" />
        <span className="font-mono text-slate-800" data-testid="cody-confidence-value">{talk.confidence_threshold}%</span>
        <span className="text-slate-500">— an Auto job whose answer scores under this goes to a person instead of writing.</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {talk.topics.map(topic => (
          <div key={topic} className="border rounded-lg p-3 space-y-1.5" data-testid={`cody-topic-${topic}`}>
            <div className="flex items-center justify-between gap-2">
              <div><div className="text-xs font-semibold text-slate-800 capitalize">{topic}</div><div className="text-[10px] text-slate-500">{TOPIC_BLURB[topic]}</div></div>
              <Button size="sm" variant="ghost" className="h-6 text-[11px]" disabled={busy === topic} onClick={() => test(topic)} data-testid={`cody-topic-test-${topic}`}>
                {busy === topic ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> Test DM</>}
              </Button>
            </div>
            <ol className="text-[11px] space-y-0.5">
              {talk.routing[topic].map((id, i) => (
                <li key={id} className="flex items-center gap-1.5" data-testid={`cody-topic-person-${topic}-${id}`}>
                  <span className="text-slate-400 w-3">{i + 1}.</span><span className="text-slate-800">{name(id)}</span>
                  <button className="text-slate-400 hover:text-rose-600" onClick={() => removePerson(topic, id)} data-testid={`cody-topic-remove-${topic}-${id}`}><X className="w-3 h-3" /></button>
                </li>
              ))}
              {!talk.routing[topic].length && <li className="text-slate-400">nobody yet — falls back to the work-group channel</li>}
            </ol>
            <Select value="" onValueChange={(id) => addPerson(topic, id)}>
              <SelectTrigger className="h-7 text-xs" data-testid={`cody-topic-add-${topic}`}><SelectValue placeholder={users.length ? "Add a person…" : "Slack users not loaded (bot token?)"} /></SelectTrigger>
              <SelectContent>{users.filter(u => !talk.routing[topic].includes(u.id)).map(u => <SelectItem key={u.id} value={u.id}>{u.name}{u.title ? ` · ${u.title}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
      {talk.interpret_rules ? (
        <details className="border rounded-lg p-2 text-[11px]" data-testid="cody-interpret-rules">
          <summary className="cursor-pointer font-medium text-slate-800">How Cody reads your replies — house rules (from the house pack, {talk.interpret_rules.split(/\s+/).length} words)</summary>
          <pre className="mt-1.5 whitespace-pre-wrap font-sans text-slate-600">{talk.interpret_rules}</pre>
        </details>
      ) : null}
      <div className="space-y-1.5" data-testid="cody-conversations">
        <div className="text-xs font-semibold text-slate-800 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Conversations</div>
        {!convs ? <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" /> : !convs.length ? <div className="text-[11px] text-slate-500">No Slack conversations yet.</div> : convs.map(c => (
          <details key={c.conv_id} className="border rounded-md p-2 text-[11px]" data-testid={`cody-conv-${c.conv_id}`}>
            <summary className="cursor-pointer flex items-center justify-between gap-2">
              <span className="truncate"><Badge variant="outline" className="text-[10px] mr-1">{c.kind}</Badge><b>{c.context?.item_name || c.ref?.batch_id || c.kind}</b> → {c.to?.name || c.channel}</span>
              <span className="shrink-0 text-slate-400">{c.state} · {fmt(c.last_at)}</span>
            </summary>
            <div className="mt-1.5 space-y-1">
              {(c.history || []).map((h, i) => (
                <div key={i} className={`whitespace-pre-wrap rounded px-2 py-1 ${h.who === "cody" ? "bg-violet-50 text-slate-700" : "bg-slate-100 text-slate-800"}`}>
                  <span className="font-medium">{h.who === "cody" ? "Cody" : h.who}</span>{h.action ? <span className="text-slate-500"> · {h.action}</span> : null}: {h.text}
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
